import { useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '@/api/client'
import { Select } from '@/components/Select'
import { SearchSelect } from '@/features/aufmass/SearchSelect'
import { useT } from '@/i18n'
import { commonMessages } from '@/i18n/messages/common'
import { projectsMessages } from '@/i18n/messages/projects'
import { projectStatuses } from './status'

interface Lookups {
  providers: { id: string; name: string | null }[]
  employees: { id: string; name: string | null }[]
}

const INPUT =
  'w-full rounded-lg border border-neutral-300 px-2.5 py-1.5 text-sm dark:border-neutral-700 dark:bg-neutral-950'

const CALC_TYPES = [
  { code: '1', label: 'calc1' },
  { code: '2', label: 'calc2' },
  { code: '3', label: 'calc3' },
  { code: '4', label: 'calc4' },
] as const

const STEPS = ['identity', 'assignment', 'planningValues'] as const

/**
 * A staged "new project" request. AU is read-only to us, so this never creates a real project —
 * it captures the fields and drops them in a queue for someone with AU access to key in. Kept
 * deliberately simple (Option B): a three-step wizard, one POST, done.
 */
export function AddProjectRequestWizard({ onClose }: { onClose: () => void }) {
  const t = useT(projectsMessages)
  const tc = useT(commonMessages)
  const qc = useQueryClient()
  const [step, setStep] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [f, setF] = useState<Record<string, string>>({})
  const set = (k: string) => (v: string) => setF((p) => ({ ...p, [k]: v }))

  const { data: lookups } = useQuery<Lookups>({
    queryKey: ['project-lookups'],
    queryFn: () => api.get<Lookups>('/api/projects/lookups'),
    staleTime: 5 * 60_000,
  })

  const providerName = (id?: string) => lookups?.providers.find((p) => p.id === id)?.name ?? null
  const employeeName = (id?: string) => lookups?.employees.find((e) => e.id === id)?.name ?? null

  const submit = useMutation({
    mutationFn: async () => {
      const num = (s?: string) => (s && s.trim() !== '' ? Number(s) : null)
      const str = (s?: string) => (s && s.trim() !== '' ? s.trim() : null)
      await api.post('/api/projects/requests', {
        projectName: f.projectName?.trim(),
        workingName: str(f.workingName), codeInternal: str(f.codeInternal), codeExternal: str(f.codeExternal),
        providerId: str(f.providerId), providerName: providerName(f.providerId),
        romId: str(f.romId), romName: employeeName(f.romId),
        projectManagerId: str(f.projectManagerId), projectManagerName: employeeName(f.projectManagerId),
        auStatus: num(f.status), country: str(f.country), calcType: num(f.calcType),
        planingStart: str(f.planingStart), planingEnd: str(f.planingEnd),
        tbStart: str(f.tbStart), tbEnd: str(f.tbEnd),
        planedMetres: num(f.planedMetres), planedHp: num(f.planedHp),
        contractValue: num(f.contractValue), projectValue: num(f.projectValue),
        priceListNote: str(f.priceListNote), contactName: str(f.contactName),
        contractNumber: str(f.contractNumber), description: str(f.description), note: str(f.note),
      })
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['project-requests'] })
      onClose()
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : t('couldNotSubmit')),
  })

  const nameOk = (f.projectName?.trim() ?? '') !== ''
  const last = step === STEPS.length - 1

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
         role="dialog" aria-modal="true" onClick={onClose}>
      <div className="flex max-h-[min(88vh,100dvh-2rem)] w-full max-w-2xl flex-col rounded-2xl bg-white shadow-xl ring-1 ring-black/5 dark:bg-neutral-900 dark:ring-white/10"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-2 border-b border-neutral-200 p-5 dark:border-neutral-800">
          <div>
            <h2 className="text-lg font-semibold">{t('newRequest')}</h2>
            <p className="mt-0.5 text-sm text-neutral-500">
              {t('newRequestSubtitle')}
            </p>
          </div>
          <button onClick={onClose} aria-label={tc('close')}
                  className="grid size-10 shrink-0 place-items-center rounded-xl text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 dark:hover:bg-neutral-800 dark:hover:text-neutral-200">✕</button>
        </div>

        {/* Stepper */}
        <div className="flex flex-wrap items-center gap-2 border-b border-neutral-200 px-5 py-3 text-xs dark:border-neutral-800">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-semibold ${
                i === step ? 'bg-brand-600 text-white'
                  : i < step ? 'bg-brand-100 text-brand-700 dark:bg-brand-500/20 dark:text-brand-300'
                  : 'bg-neutral-200 text-neutral-500 dark:bg-neutral-800'
              }`}>{i + 1}</span>
              <span className={i === step ? 'font-medium' : 'text-neutral-500'}>{t(s)}</span>
              {i < STEPS.length - 1 && <span className="text-neutral-300 dark:text-neutral-700">→</span>}
            </div>
          ))}
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-5">
          {step === 0 && (
            <>
              <Field label={t('projectNameRequired')}>
                <input className={INPUT} value={f.projectName ?? ''} autoFocus
                       onChange={(e) => set('projectName')(e.target.value)} />
              </Field>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label={t('workingName')}><input className={INPUT} value={f.workingName ?? ''} onChange={(e) => set('workingName')(e.target.value)} /></Field>
                <Field label={t('country')}><input className={INPUT} value={f.country ?? ''} onChange={(e) => set('country')(e.target.value)} /></Field>
                <Field label={t('codeInternal')}><input className={INPUT} value={f.codeInternal ?? ''} onChange={(e) => set('codeInternal')(e.target.value)} /></Field>
                <Field label={t('codeExternal')}><input className={INPUT} value={f.codeExternal ?? ''} onChange={(e) => set('codeExternal')(e.target.value)} /></Field>
              </div>
              <Field label={t('status')}>
                <Select value={f.status ?? ''} onChange={set('status')} empty={{ value: '', label: t('noneOption') }}
                  options={projectStatuses().map((s) => ({ value: String(s.code), label: `${s.code} · ${s.label}` }))} />
              </Field>
              <Field label={t('description')}><textarea rows={3} className={INPUT} value={f.description ?? ''} onChange={(e) => set('description')(e.target.value)} /></Field>
            </>
          )}

          {step === 1 && (
            <>
              <Field label={t('provider')}>
                <SearchSelect value={f.providerId ?? ''} onChange={set('providerId')}
                              options={lookups?.providers ?? []} allLabel={t('noneOption')} placeholder={t('findProvider')} />
              </Field>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label={t('rolloutManager')}>
                  <SearchSelect value={f.romId ?? ''} onChange={set('romId')}
                                options={lookups?.employees ?? []} allLabel={t('noneOption')} placeholder={t('findPerson')} />
                </Field>
                <Field label={t('projectManager')}>
                  <SearchSelect value={f.projectManagerId ?? ''} onChange={set('projectManagerId')}
                                options={lookups?.employees ?? []} allLabel={t('noneOption')} placeholder={t('findPerson')} />
                </Field>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label={t('contactName')}><input className={INPUT} value={f.contactName ?? ''} onChange={(e) => set('contactName')(e.target.value)} /></Field>
                <Field label={t('contractNumber')}><input className={INPUT} value={f.contractNumber ?? ''} onChange={(e) => set('contractNumber')(e.target.value)} /></Field>
              </div>
              <p className="text-xs text-neutral-500">
                {t('pickedFromAu')}
              </p>
            </>
          )}

          {step === 2 && (
            <>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label={t('calcType')}>
                  <Select value={f.calcType ?? ''} onChange={set('calcType')} empty={{ value: '', label: t('noneOption') }}
                    options={CALC_TYPES.map((c) => ({ value: c.code, label: t(c.label) }))} />
                </Field>
                <Field label={t('priceListNote')}><input className={INPUT} value={f.priceListNote ?? ''} placeholder={t('priceListPlaceholder')} onChange={(e) => set('priceListNote')(e.target.value)} /></Field>
                <Field label={t('planningStart')}><input type="date" className={INPUT} value={f.planingStart ?? ''} onChange={(e) => set('planingStart')(e.target.value)} /></Field>
                <Field label={t('planningEnd')}><input type="date" className={INPUT} value={f.planingEnd ?? ''} onChange={(e) => set('planingEnd')(e.target.value)} /></Field>
                <Field label={t('tbStart')}><input type="date" className={INPUT} value={f.tbStart ?? ''} onChange={(e) => set('tbStart')(e.target.value)} /></Field>
                <Field label={t('tbEnd')}><input type="date" className={INPUT} value={f.tbEnd ?? ''} onChange={(e) => set('tbEnd')(e.target.value)} /></Field>
                <Field label={t('tbPlannedM')}><input type="number" className={INPUT} value={f.planedMetres ?? ''} onChange={(e) => set('planedMetres')(e.target.value)} /></Field>
                <Field label={t('hpPlannedPcs')}><input type="number" className={INPUT} value={f.planedHp ?? ''} onChange={(e) => set('planedHp')(e.target.value)} /></Field>
                <Field label={t('contractValueEur')}><input type="number" className={INPUT} value={f.contractValue ?? ''} onChange={(e) => set('contractValue')(e.target.value)} /></Field>
                <Field label={t('projectValueEur')}><input type="number" className={INPUT} value={f.projectValue ?? ''} onChange={(e) => set('projectValue')(e.target.value)} /></Field>
              </div>
              <Field label={t('noteForKeyer')}><textarea rows={2} className={INPUT} value={f.note ?? ''} onChange={(e) => set('note')(e.target.value)} /></Field>
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-neutral-200 p-5 dark:border-neutral-800">
          {error
            ? <p role="alert" className="text-sm" style={{ color: 'var(--viz-critical)' }}>{error}</p>
            : <span className="text-xs text-neutral-400">{t('stepOf', { step: step + 1, total: STEPS.length })}</span>}
          <div className="flex gap-2">
            <button onClick={step === 0 ? onClose : () => setStep((s) => s - 1)}
                    className="rounded-lg px-3 py-1.5 text-sm text-neutral-600 ring-1 ring-neutral-300 hover:bg-neutral-100 dark:text-neutral-300 dark:ring-neutral-700 dark:hover:bg-neutral-800">
              {step === 0 ? tc('cancel') : t('back')}
            </button>
            {last ? (
              <button onClick={() => { setError(null); submit.mutate() }} disabled={!nameOk || submit.isPending}
                      className="rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-500 disabled:opacity-60">
                {submit.isPending ? t('submitting') : t('submitRequest')}
              </button>
            ) : (
              <button onClick={() => setStep((s) => s + 1)} disabled={step === 0 && !nameOk}
                      className="rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-500 disabled:opacity-60">
                {t('next')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-neutral-600 dark:text-neutral-300">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  )
}
